import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  AudioSettings,
  defaultAudioSettings,
  setMasterVolume,
  setMusicVolume,
  setSfxVolume,
  initAudio,
} from '@/systems/audioSystem'
import {
  playTrack,
  stopMusicPlayer,
} from '@/systems/musicPlayer'
import {
  MUSIC_LIBRARY,
  DEFAULT_CATEGORY_ID,
  DEFAULT_MUSIC_TRACK_ID,
  MusicCategoryId,
  getTrack,
  getTracksByCategory,
  nextTrackId,
  prevTrackId,
} from '@/config/musicLibrary'

interface AudioState extends AudioSettings {
  /** Selected track id (persisted) */
  currentTrackId: string
  /** Active category scope / playlist (persisted) */
  currentCategoryId: MusicCategoryId
  /** Whether the music player is currently playing (transient) */
  isMusicPlaying: boolean
  /** Whether the floating mini-player is hidden from the layout (persisted) */
  miniPlayerHidden: boolean
  /** Whether the "you can re-enable it in settings" reminder was shown (persisted) */
  miniPlayerHintShown: boolean

  setMasterVol: (vol: number) => void
  setMusicVol: (vol: number) => void
  setSfxVol: (vol: number) => void
  toggleMusic: () => void
  toggleSfx: () => void
  initializeAudio: () => void
  /** Start the currently selected track (no-op if music disabled) */
  playMusic: () => void
  stopMusic: () => void
  /** Select + play a specific track */
  playTrackById: (id: string) => void
  /** Set the active category (playlist scope) for next/prev */
  setCategory: (catId: MusicCategoryId) => void
  /** Toggle playback of the selected track */
  togglePlay: () => void
  nextTrack: () => void
  prevTrack: () => void
  /** Hide the on-screen mini-player (music keeps playing) */
  hideMiniPlayer: () => void
  /** Restore the on-screen mini-player */
  showMiniPlayer: () => void
}

export const useAudioStore = create<AudioState>()(
  persist(
    (set, get) => ({
      ...defaultAudioSettings,
      currentTrackId: DEFAULT_MUSIC_TRACK_ID,
      currentCategoryId: DEFAULT_CATEGORY_ID,
      isMusicPlaying: false,
      miniPlayerHidden: false,
      miniPlayerHintShown: false,

      setMasterVol: (vol) => {
        setMasterVolume(vol)
        set({ masterVolume: vol })
      },

      setMusicVol: (vol) => {
        setMusicVolume(vol)
        set({ musicVolume: vol })
      },

      setSfxVol: (vol) => {
        setSfxVolume(vol)
        set({ sfxVolume: vol })
      },

      toggleMusic: () => {
        const next = !get().musicEnabled
        set({ musicEnabled: next })
        if (next) {
          get().initializeAudio()
          playTrack(getTrack(get().currentTrackId))
          set({ isMusicPlaying: true })
        } else {
          stopMusicPlayer()
          set({ isMusicPlaying: false })
        }
      },

      toggleSfx: () => set({ sfxEnabled: !get().sfxEnabled }),

      initializeAudio: () => {
        initAudio()
        const { masterVolume, musicVolume, sfxVolume } = get()
        setMasterVolume(masterVolume)
        setMusicVolume(musicVolume)
        setSfxVolume(sfxVolume)
      },

      playMusic: () => {
        if (!get().musicEnabled) return
        get().initializeAudio()
        playTrack(getTrack(get().currentTrackId))
        set({ isMusicPlaying: true })
      },

      stopMusic: () => {
        stopMusicPlayer()
        set({ isMusicPlaying: false })
      },

      playTrackById: (id) => {
        const track = MUSIC_LIBRARY.find(t => t.id === id) ?? getTrack(id)
        get().initializeAudio()
        set({ currentTrackId: track.id, musicEnabled: true })
        playTrack(track)
        set({ isMusicPlaying: true })
      },

      setCategory: (catId) => {
        set({ currentCategoryId: catId })
        // If the current track falls outside the new scope, jump to the first of
        // the category (and follow it with playback only if music is sounding).
        const scope = getTracksByCategory(catId)
        if (scope.length && !scope.some(t => t.id === get().currentTrackId)) {
          const first = scope[0]
          set({ currentTrackId: first.id })
          if (get().isMusicPlaying) playTrack(first)
        }
      },

      togglePlay: () => {
        if (get().isMusicPlaying) {
          stopMusicPlayer()
          set({ isMusicPlaying: false })
        } else {
          get().initializeAudio()
          playTrack(getTrack(get().currentTrackId))
          set({ isMusicPlaying: true, musicEnabled: true })
        }
      },

      nextTrack: () => {
        const id = nextTrackId(get().currentTrackId, get().currentCategoryId)
        set({ currentTrackId: id })
        if (get().isMusicPlaying) playTrack(getTrack(id))
      },

      prevTrack: () => {
        const id = prevTrackId(get().currentTrackId, get().currentCategoryId)
        set({ currentTrackId: id })
        if (get().isMusicPlaying) playTrack(getTrack(id))
      },

      // Hiding only removes the UI strip; playback is independent (toggleMusic).
      // We flag the reminder as shown so the one-time toast never repeats.
      hideMiniPlayer: () => set({ miniPlayerHidden: true, miniPlayerHintShown: true }),
      showMiniPlayer: () => set({ miniPlayerHidden: false }),
    }),
    {
      name: 'padel-audio-settings',
      partialize: (state) => ({
        masterVolume: state.masterVolume,
        musicVolume: state.musicVolume,
        sfxVolume: state.sfxVolume,
        musicEnabled: state.musicEnabled,
        sfxEnabled: state.sfxEnabled,
        currentTrackId: state.currentTrackId,
        currentCategoryId: state.currentCategoryId,
        miniPlayerHidden: state.miniPlayerHidden,
        miniPlayerHintShown: state.miniPlayerHintShown,
      }),
    }
  )
)
